import { Component, ElementRef, ViewChild } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { AlertService } from '../../../../../shared/alert/alert.service';
import * as XLSX from 'xlsx';
import { ImportDesignMapComponent } from '../import-design-map/import-design-map.component';
import { ImportDesignContext } from '../import-design-context';

interface ImportFormat {
    name: string;
    extension: string;
    isVisible: boolean;
}

@Component({
    selector: 'jhi-import-design-modal',
    templateUrl: './import-design-modal.component.html',
    styleUrl: './import-design-modal.component.scss'
})
export class ImportDesignModalComponent {

    @ViewChild('fileUpload') fileUpload!: ElementRef;

    title: string;

    file: File | null = null;
    importedData: any[] | null = null;

    importFormats: ImportFormat[] = [];
    selectedFormat: ImportFormat | null = null;

    private destroy$ = new Subject<void>();
    isSaving: boolean;

    constructor(
        public modalService: NgbModal,
        public activeModal: NgbActiveModal,
        private alertService: AlertService,
        private importDesignContext: ImportDesignContext
    ) {
    }

    ngOnInit(): void {
        this.initImportFormats();
        this.selectedFormat = this.importFormats[0];
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    initImportFormats(): void {
        this.importFormats = [
            { name: 'CSV', extension: '.csv', isVisible: true },
            { name: 'Excel', extension: '.xls,.xlsx', isVisible: true }
        ];
    }

    cancel(): void {
        this.activeModal.dismiss('back');
    }

    clearSelectedFile(): void {
        this.file = null;
        this.importedData = null;
    }

    submitImport(): void {
        this.showDesignMapPopup();
    }

    close(): void {
        this.activeModal.close();
    }

    showDesignMapPopup(): void {
        this.importDesignContext.unmappedItems = this.importedData[0].map((value, index) => {
            return {
                id: String(index),
                name: value
            };
        });
        this.importDesignContext.importedData = this.importedData;
        this.importDesignContext.designSource = this.file.name;
        const modalRef = this.modalService.open(ImportDesignMapComponent as Component, { modalDialogClass: 'modal-autofit', backdrop: 'static' });
        modalRef.result.then(() => {
            this.activeModal.close();
        }, () => {
            this.activeModal.close();
        });
    }

    handleFileChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        if (!target.files?.length) {
            return;
        }

        const file = target.files[0];
        const reader = new FileReader();

        reader.onload = (e: any) => {
            try {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary', raw: true });

                // grab first sheet
                let wsname = wb.SheetNames[0];
                let parseData = this.parseFile(wb, wsname);

                // update component state
                this.importedData = parseData;
                this.file = file;

                // Truncate filename if too long
                const length = 20;
                if (this.file && this.file.name.length > length) {
                    (this.file as any).abbrName = this.file.name.substring(0, length) + '...';
                } else if (this.file) {
                    (this.file as any).abbrName = this.file.name;
                }
            } catch (error) {
                this.alertService.error('study-editor.import-design.import-file.error.reading-file');
                console.error(error);
            }
        };

        reader.readAsBinaryString(file);
    }

    private parseFile(wb: XLSX.WorkBook, wsname: string): any[] {
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
        return data.map((row: any[]) =>
            row.map((cell: any) => this.stripHtmlTags(cell?.toString() || ''))
        );
    }

    private stripHtmlTags(str: string): string {
        // Remove Tag HTML
        str = str.replace(/<\/?[^>]+(>|$)/g, '');
        // Remove special characters: <, >, ?, % |
        return str.replace(/[<>?%|]/g, '');
    }

}
